import React from 'react';
import { FaQq, FaGithub, FaAngleDown, FaTelegramPlane, FaTwitter } from "react-icons/fa";
import { FiCopy, FiAlertOctagon } from 'react-icons/fi';
import { AnimatePresence, motion, useWillChange } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Skeleton, Tooltip, useDisclosure } from "@nextui-org/react";
import { TRANSITION_VARIANTS } from "@nextui-org/framer-transitions";

function ProjectItem({ project }) {
  const handleCopyCA = (fullContractAddress) => {
    // 复制完整的合约地址到剪贴板
    navigator.clipboard.writeText(fullContractAddress)
      .then(() => {
        alert('合约地址已复制到剪贴板！');
      })
      .catch(err => {
        console.error('复制失败: ', err);
        alert('复制合约地址失败，请手动复制。');
      });
  };
    const willChange = useWillChange()
  const { isOpen, onOpenChange } = useDisclosure()
  return (<div key={project.fullContractAddress} className="bg-gray-100 dark:bg-box p-4 rounded-xl shadow-xl mb-6" style={{ boxShadow: "inset 0 3px 3px rgba(0, 0, 0, 0.5)" }}>

    {/* 顶部区域: 项目Logo和详细信息 */}
    <div className="flex  items-start md:items-center gap-6 mb-2" >
      {/* 项目Logo */}
      <div className="flex-shrink-0 w-28 h-28 md:w-40 md:h-40 rounded-full flex flex-col items-center justify-center text-button font-bold text-xs">
        <img className={'max-w-full max-h-full object-cover rounded-full'} alt={''} src={project.logo} />
      </div>

      {/* 详细信息 */}
      <div className="flex-grow">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xl font-bold">{project.projectName}</p>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full 
                ${project.status === '未上线' ? 'bg-button text-black' : 'bg-green-500 text-white'}`}>
            状态:{project.status}
          </span>
        </div>


        <p className="text-button text-xl font-bold mb-2 text-nowrap"> <span className="text-xs">上线时间:</span>{project.launchTime}</p>
        <div className="flex items-center mb-4">
          <p className=" mr-2">CA: {project.contractAddress}</p>
          <button onClick={() => handleCopyCA(project.fullContractAddress)} className="text-button hover:text-yellow-300 focus:outline-none">
            <FiCopy size={20} />
          </button>
        </div>


      </div>
    </div>
    {/* 中间区域: 项目名称和简介按钮 */}
    <div className="w-full gap-4 mb-2">

      <button href={project.projectBriefLink} className="min-w-full border border-button text-white  text-black text-xs font-semibold py-3 rounded-md transition-colors text-center focus:outline-none" onClick={onOpenChange} >
        点击查看项目简介<FaAngleDown className='inline-block ml-1' />
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.section
              key="accordion-content"
              animate="enter"
              exit="exit"
              initial="exit"
              style={{ overflowY: "hidden", willChange }}
              variants={TRANSITION_VARIANTS.collapse}
            >
              <div className={'gap-2 mt-4 text-sm'}>
                <div className={'text-slate-500 indent-8 text-left px-4 leading-8'}>
                  {project.description}
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </button>
    </div>
    {/* 统计数据网格 */}
    <div className="grid grid-cols-4 sm:grid-cols-4 gap-2 text-center text-sm mb-2">
      <div className="bg-black py-2 rounded-md border border-button">
        <p className="text-gray-400 text-xs">所属链</p>
        <p className="text-white font-medium">{project.chain}</p>
      </div>
      <div className="bg-black py-2 rounded-md border border-button">
        <p className="text-gray-400 text-xs">发行量</p>
        <p className="text-white font-medium">{project.supply}</p>
      </div>
      <div className="bg-black py-2 rounded-md border border-button">
        <p className="text-gray-400 text-xs">买税</p>
        <p className="text-white font-medium">{project.buyTax}</p>
      </div>
      <div className="bg-black py-2 rounded-md border border-button">
        <p className="text-gray-400 text-xs">卖税</p>
        <p className="text-white font-medium">{project.sellTax}</p>
      </div>
    </div>


    {/* 底部区域: 参与按钮和社交链接 */}
    <div className="flex  grid-cols-2  items-center justify-between gap-4">
      <a href={project.participateLink} className="bg-button hover:bg-yellow-500 text-black text-xs font-bold py-3  rounded-lg w-full sm:w-auto text-center transition-colors focus:outline-none">
        立即参与
      </a>
      <div className="flex gap-3">
        {/* 社交链接按钮 (根据图片只显示文本，如果需要图标可以在这里添加) */}
        <a href={project.telegramLink} className=" text-button w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold hover:bg-yellow-600/30 transition-colors focus:outline-none">
          <FaTelegramPlane className={'flex-shrink-0 size-7'} />
        </a>
        <a href={project.twitterLink} className="text-button w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold hover:bg-yellow-600/30 transition-colors focus:outline-none">
          <FaTwitter className={'flex-shrink-0 size-7'} />
        </a>
        <a href={project.qqLink} className="text-button w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold hover:bg-yellow-600/30 transition-colors focus:outline-none">
          <FaQq className={'flex-shrink-0 size-7'} />
        </a>
      </div>
    </div>
  </div>)
}

function ProjectCard() {
  // 定义项目数据，方便管理和修改
  const projectData = [{
    logo: '/images/logo/farm.png', // 项目Logo的路径
    launchTime: '2026-01-18',
    status: '已上线', // 可以是 '未上线' 或 '已上线'
    contractAddress: '0x8d5*********33333',
    fullContractAddress: '0x8d5abcdefg1234567890abcdefg33333333', // 完整的合约地址用于复制
    chain: 'BSC',
    supply: '0亿',
    buyTax: '0%',
    sellTax: '0%',
    projectName: 'W3农场',
    projectBriefLink: '#', // 实际链接
    participateLink: 'https://farm.w3swap.vip/', // 实际链接
    telegramLink: '#', // 实际链接
    twitterLink: '#', // 实际链接
    qqLink: '#', // 实际链接
    description: 'W3 农场是W3 生态专属理财玩法，以 W3 代币为唯一参与凭证，在 BSC 链上实现 “玩赚一体” 的链上理财体验。玩家只需用 W3 代币兑换土地使用权、购买不同等级种子，即可开启 “播种生息” 模式。种子等级越高，日收益比例越可观，组队参与还能额外获得 25% 收益加成。收益以 W3 代币结算，既能享受稳定日收益，又能同步把握 W3 代币增值红利。',
  }, 
  {
    logo: 'https://www.4burn.vip/_nuxt/img/logo.127e7b8.png', // 项目Logo的路径
    launchTime: '已上线',
    status: '已上线', // 可以是 '未上线' 或 '已上线'
    contractAddress: '0xea6*********7ffff',
    fullContractAddress: '0xea6aa3a039f442f572a5bf31c63ad7ad89a7ffff', // 完整的合约地址用于复制
    chain: 'BSC',
    supply: '10亿',
    buyTax: '3%',
    sellTax: '3%',
    projectName: '4Burn',
    projectBriefLink: '#', // 实际链接
    participateLink: 'https://www.4burn.vip/', // 实际链接
    telegramLink: 'https://t.me/jmsq168', // 实际链接
    twitterLink: 'https://x.com/ranshaoplus', // 实际链接
    qqLink: 'https://qun.qq.com/universal-share/share?ac=1&authKey=qQimbo%2BuYpiPRTTQPXgCarKUM/HBmVOwXX7x0xWqJpqFcDuqEuZ7RJqGP2zqso%2Bw&busi_data=eyJncm91cENvZGUiOiIxMDIzNzcxNzM2IiwidG9rZW4iOiJWQXlEcCszVzRhQmxIdHpmVk9wK0hobHNsb2wrZmZzSWxXcGpockhUM1U3WTNYajFTM0NiWDl0c25oSDQ5czhPIiwidWluIjoiMTQyNjQ2NDQifQ==&data=zWTme-V2eEuxaDXcc2P8uIKatriGlDycl-xKKf1zEuc6sHk4g4VFGTr34qiacj9012BHd4nKerJ5FqW31FeE8Q&svctype=4&tempid=h5_group_info', // 实际链接
    description: '🔥万币起燃，万倍锋芒！金马社区燃爆机制重磅来袭！4Burn作为Four平台税币板块首个创新机制Meme燃烧分红、娱乐代币，依托金马社区、PEPE社区、盘古社区三大头部社区的强强联合，整合全国百大地推社区的扎实网络与众多行业KOL的全域流量，以成熟的生态共识与强大的共建合力强势布局，全力冲击币安Alpha，打造兼具稀缺价值与成长潜力的顶级Meme代币！',
  },
  {
    logo: '/tokens/0x4619e5932ee96b317808e15d53824b97b6b94444.png', // 项目Logo的路径
    launchTime: '已上线',
    status: '已上线', // 可以是 '未上线' 或 '已上线'
    contractAddress: '0x461*********94444',
    fullContractAddress: '0x4619e5932ee96b317808e15d53824b97b6b94444', // 完整的合约地址用于复制
    chain: 'BSC',
    supply: '31888',
    buyTax: '6%',
    sellTax: '10%',
    projectName: 'Xone',
    projectBriefLink: '#', // 实际链接
    participateLink: '#', // 实际链接
    telegramLink: '#', // 实际链接
    twitterLink: '#', // 实际链接
    qqLink: '#', // 实际链接
    description: 'Xone on-chain Bank(Xone链上银行)Usdt进出每日赚利息，日化2.5%(玩法参与进群联系管理)提现消耗xone，币价永续上涨\n无链接，无APP，无DAPP。链上智能合约自动执行。安全，公开，透明，可追溯。不可篡改。',
  }];
  const { t } = useTranslation()



  return (
    <div className="min-h-screen max-w-lg mx-auto bg-background text-white p-4 flex flex-col" >
      {/* 主卡片容器 */}
      {projectData.map((project, index) => (
        <ProjectItem key={index} project={project} />
      ))}
      <div className="bg-gray-100 dark:bg-box p-4 rounded-xl shadow-xl mb-6" style={{ boxShadow: "inset 0 3px 3px rgba(0, 0, 0, 0.5)" }}>


        {/* 中间区域: 项目名称和简介按钮 */}
        <div className="grid grid-cols-3 gap-4 mb-2">
          <div className='col-span-2 text-red-500 text-xs'>*以上内容均为用户上传仅供查询了解平台不作为投资建议请自行掌控风险</div>
          <button className="bg-button text-black text-xs font-semibold py-3 rounded-md hover:bg-gray-800 transition-colors text-center focus:outline-none">
            项目申请
          </button>
        </div>


      </div>
    </div>
  );
}

export default ProjectCard;
